import { CloseOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Flex, Form, Typography, Upload } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type RcFile, type UploadChangeParam } from "antd/es/upload/interface"
import React, { type ComponentProps, useState } from "react"

import { validateFile } from "../../../../../utils/file-validation.ts"
import { getBase64 } from "../../../../../utils/utils"
import { type SettingsCompanyFormData } from "../SettingsCompanyForm.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Text } = Typography

const MAX_SIZE_MB = 10
const ALLOWED_FILE_EXTENSIONS = ["jpeg", "jpg", "png"]

interface CompanyBrandingFormSectionProps {
  form: FormInstance<SettingsCompanyFormData>
  initialValues?: SettingsCompanyFormData
}

export function CompanyBrandingFormSection({
  form,
  initialValues
}: CompanyBrandingFormSectionProps) {
  const key: keyof SettingsCompanyFormData = "logo"

  const [logoUrl, setLogoUrl] = useState<string | null>(
    initialValues?.logo && typeof initialValues?.logo === "string"
      ? initialValues?.logo
      : null
  )

  const watchLogo = Form.useWatch(key, form)
  const logoSrc =
    typeof watchLogo === "string" || !watchLogo ? watchLogo : logoUrl

  const onChangeLogo = (info: UploadChangeParam) => {
    const { file } = info

    if (file.status === "done") {
      getBase64(file.originFileObj, (imageUrl: string) => {
        setLogoUrl(imageUrl)
        form.setFieldsValue({ [key]: file.originFileObj })
      })
    }
  }

  const onDeleteLogo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setLogoUrl(null)
    form.setFieldsValue({ [key]: null })
  }

  const beforeUpload = (file: RcFile) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true,
      sizeMessage: `This logo is larger than ${MAX_SIZE_MB}MB. Please choose a smaller file.`
    })
  }

  const uploadProps: ComponentProps<typeof Upload> = {
    name: key,
    showUploadList: false,
    multiple: false,
    accept: ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    maxCount: 1,
    beforeUpload: beforeUpload,
    onChange: onChangeLogo,
    customRequest: ({ onSuccess }) => onSuccess && setTimeout(onSuccess, 300)
  }

  return (
    <>
      <Form.Item<SettingsCompanyFormData> label={"Company Logo"} name={key}>
        <Upload {...uploadProps} className={"cursor-pointer"}>
          <Flex gap={"middle"}>
            <div className={"relative"}>
              <Avatar
                shape="square"
                size={64}
                icon={<UserOutlined />}
                src={logoSrc}
              />
              {logoSrc && (
                <Button
                  onClick={onDeleteLogo}
                  size={"small"}
                  className={"absolute -bottom-1 -right-1 rounded-md"}
                  icon={<CloseOutlined />}
                />
              )}
            </div>

            <Flex vertical gap={"small"}>
              <Button className={"max-w-1/2"} icon={<UploadOutlined />}>
                Upload Logo
              </Button>
              <Text type={"secondary"} className={"text-xs"}>
                {ALLOWED_FILE_EXTENSIONS?.join(", ")?.toUpperCase()} • Up to{" "}
                {MAX_SIZE_MB}MB • Recommended: 512×512 (square)
              </Text>
            </Flex>
          </Flex>
        </Upload>
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"Website"}
        name="company_website"
        rules={[
          {
            type: "url",
            message: "Please enter a valid URL"
          }
        ]}
      >
        <TextInput disableAutoCapitalize placeholder={"Enter website"} />
      </Form.Item>
    </>
  )
}
